// src/pages/StaffRoster.js
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, CircularProgress, Select, MenuItem,
  Tooltip, Chip, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Grid, Card, CardContent
} from '@mui/material';
import {
  ArrowBackIosNew, ArrowForwardIos, PictureAsPdf,
  ContentCopy, PlaylistAdd, Group
} from '@mui/icons-material';

import { getData, saveData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';

import pdfMake from "pdfmake/build/pdfmake";
import { vfs } from "pdfmake/build/vfs_fonts";
pdfMake.vfs = vfs;

// Опції для випадаючого списку в графіку
const SHIFT_OPTIONS = [
  { value: 'M', label: 'Mañana', color: '#e3f2fd', short: 'M' },
  { value: 'T', label: 'Tarde', color: '#fff3e0', short: 'T' },
  { value: 'OFF', label: 'Libre', color: '#f3e5f5', short: 'L' },
  { value: 'VAC', label: 'Vacaciones', color: '#e8f5e9', short: 'V' },
  { value: 'SICK', label: 'Baja', color: '#ffebee', short: 'B' },
  { value: '', label: '—', color: 'transparent', short: '' },
];

// Шаблони змін
const SHIFT_TEMPLATES = {
  morning_shift: {
    name: 'Morning Shift',
    pattern: ['M', 'M', 'M', 'M', 'M', 'OFF', 'OFF']
  },
  evening_shift: {
    name: 'Evening Shift',
    pattern: ['T', 'T', 'T', 'T', 'T', 'OFF', 'OFF']
  },
  rotating_shift: {
    name: 'Rotating Shift',
    pattern: ['M', 'M', 'T', 'T', 'OFF', 'M', 'M']
  }
};

// Функції для роботи з датами
const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const formatDate = (date, formatType = 'short') => {
  const d = new Date(date);
  
  if (formatType === 'weekday') {
    const weekdays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    return weekdays[d.getDay()];
  }
  
  if (formatType === 'date') {
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  }
  
  if (formatType === 'full') {
    return d.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  }
  
  return d.toISOString().split('T')[0];
};

const getWeekDays = (startDate) => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    days.push(addDays(startDate, i));
  }
  return days;
};

const StaffRosterPage = () => {
  const { t } = useI18n();
  const [staff, setStaff] = useState([]);
  const [roster, setRoster] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Стани для масових дій
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [copyWeekDialog, setCopyWeekDialog] = useState(false);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedShift, setSelectedShift] = useState('M');

  const startOfCurrentWeek = getStartOfWeek(currentDate);
  const weekDays = getWeekDays(startOfCurrentWeek);
  const weekKey = formatDate(startOfCurrentWeek);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const staffData = await getData('staff') || [];
        const rosterData = await getData('staff_roster') || {};
        
        const activeStaff = staffData.filter(s => s.status === 'active');
        setStaff(activeStaff);
        setRoster(rosterData);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleRosterChange = async (employeeId, dateKey, value) => {
    const newRoster = { ...roster };
    if (!newRoster[weekKey]) {
      newRoster[weekKey] = {};
    }
    if (!newRoster[weekKey][employeeId]) {
      newRoster[weekKey][employeeId] = {};
    }
    newRoster[weekKey][employeeId][dateKey] = value;
    
    setRoster(newRoster);
    await saveData('staff_roster', newRoster);
  };
  
  const changeWeek = (weeks) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (weeks * 7));
    setCurrentDate(newDate);
  };

  // Нові функції для масових дій

  // Масове призначення зміни
  const applyBulkShift = async (shiftType, daysToApply = []) => {
    const newRoster = { ...roster };
    if (!newRoster[weekKey]) newRoster[weekKey] = {};

    staff.forEach(employee => {
      if (!newRoster[weekKey][employee.id]) newRoster[weekKey][employee.id] = {};
      
      weekDays.forEach((day, index) => {
        if (daysToApply.length > 0 && !daysToApply.includes(index)) return;
        
        const dateKey = formatDate(day);
        newRoster[weekKey][employee.id][dateKey] = shiftType;
      });
    });

    setRoster(newRoster);
    await saveData('staff_roster', newRoster);
    setBulkActionDialog(false);
  };

  // Копіювання тижня
  const copyFromPreviousWeek = async () => {
    const prevWeekStart = addDays(startOfCurrentWeek, -7);
    const prevWeekKey = formatDate(prevWeekStart);
    const prevWeekRoster = roster[prevWeekKey];

    if (!prevWeekRoster) {
      alert(t('staff_roster.no_previous_week'));
      return;
    }

    const newRoster = { ...roster };
    newRoster[weekKey] = { ...prevWeekRoster };
    
    setRoster(newRoster);
    await saveData('staff_roster', newRoster);
    setCopyWeekDialog(false);
  };

  // Застосування шаблону
  const applyTemplate = async (templateName) => {
    const template = SHIFT_TEMPLATES[templateName];
    if (!template) return;

    const newRoster = { ...roster };
    if (!newRoster[weekKey]) newRoster[weekKey] = {};

    staff.forEach(employee => {
      if (!newRoster[weekKey][employee.id]) newRoster[weekKey][employee.id] = {};
      
      weekDays.forEach((day, index) => {
        const dateKey = formatDate(day);
        newRoster[weekKey][employee.id][dateKey] = template.pattern[index] || '';
      });
    });

    setRoster(newRoster);
    await saveData('staff_roster', newRoster);
    setTemplateDialog(false);
    setSelectedTemplate('');
  };

  // Статистика змін
  const getShiftStats = () => {
    const stats = {};
    SHIFT_OPTIONS.forEach(shift => {
      stats[shift.value] = 0;
    });

    Object.values(roster[weekKey] || {}).forEach(employeeShifts => {
      Object.values(employeeShifts).forEach(shift => {
        if (shift && stats[shift] !== undefined) {
          stats[shift]++;
        }
      });
    });

    return stats;
  };

  const generatePdf = () => {
    const tableHeader = [
      { text: t('staff_roster.employee'), bold: true },
      { text: t('staff_roster.position_area'), bold: true },
      ...weekDays.map(day => ({ 
        text: `${formatDate(day, 'weekday')}\n${formatDate(day, 'date')}`, 
        bold: true, 
        alignment: 'center' 
      }))
    ];

    const tableBody = staff.map(employee => {
      const employeeWeekData = roster[weekKey]?.[employee.id] || {};
      return [
        { text: employee.name || `${employee.firstName} ${employee.lastName}`, bold: true },
        { text: employee.department || employee.position || '-', fontSize: 9 },
        ...weekDays.map(day => {
          const dateKey = formatDate(day);
          const shiftValue = employeeWeekData[dateKey] || '';
          const shiftOption = SHIFT_OPTIONS.find(opt => opt.value === shiftValue);
          return { 
            text: shiftOption?.short || '', 
            alignment: 'center',
            fontSize: 10
          };
        })
      ];
    });

    const docDefinition = {
      content: [
        { text: t('staff_roster.title'), style: 'header' },
        { text: `${t('staff_roster.week_of')} ${formatDate(startOfCurrentWeek, 'full')}`, style: 'subheader' },
        {
          table: {
            headerRows: 1,
            widths: ['*', '*', ...Array(7).fill('auto')],
            body: [tableHeader, ...tableBody]
          },
          layout: 'lightHorizontalLines'
        }
      ],
      styles: {
        header: { fontSize: 18, bold: true, alignment: 'center', margin: [0, 0, 0, 10] },
        subheader: { fontSize: 12, alignment: 'center', margin: [0, 0, 0, 15] }
      },
      defaultStyle: {
        fontSize: 10,
      }
    };
    
    const fileName = `Horario_${formatDate(startOfCurrentWeek)}.pdf`;
    pdfMake.createPdf(docDefinition).download(fileName);
  };

  const shiftStats = getShiftStats();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2, borderRadius: '12px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">{t('staff_roster.title')}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <IconButton onClick={() => changeWeek(-1)} size="small">
            <ArrowBackIosNew fontSize="small" />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: { xs: '100%', sm: '280px' }, textAlign: 'center', fontSize: '1.1rem' }}>
            {t('staff_roster.week_of')} {formatDate(startOfCurrentWeek, 'full')}
          </Typography>
          <IconButton onClick={() => changeWeek(1)} size="small">
            <ArrowForwardIos fontSize="small" />
          </IconButton>
          
          {/* Кнопки масових дій */}
          <Tooltip title={t('staff_roster.bulk_actions')}>
            <Button 
              variant="outlined" 
              startIcon={<Group />}
              onClick={() => setBulkActionDialog(true)}
              size="small"
            >
              {t('staff_roster.bulk_actions')}
            </Button>
          </Tooltip>

          <Tooltip title={t('staff_roster.copy_week')}>
            <Button 
              variant="outlined" 
              startIcon={<ContentCopy />}
              onClick={() => setCopyWeekDialog(true)}
              size="small"
            >
              {t('staff_roster.copy')}
            </Button>
          </Tooltip>

          <Tooltip title={t('staff_roster.templates')}>
            <Button 
              variant="outlined" 
              startIcon={<PlaylistAdd />}
              onClick={() => setTemplateDialog(true)}
              size="small"
            >
              {t('staff_roster.templates')}
            </Button>
          </Tooltip>

          <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={generatePdf} size="small">
            {t('staff_roster.export_pdf')}
          </Button>
        </Box>
      </Box>

      {/* Статистика змін */}
      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            {SHIFT_OPTIONS.filter(shift => shift.value).map(shift => (
              <Chip
                key={shift.value}
                label={`${shift.short}: ${shiftStats[shift.value] || 0}`}
                size="small"
                sx={{ 
                  backgroundColor: shift.color,
                  fontWeight: 'bold'
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {staff.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('staff_roster.no_staff_available')}
        </Alert>
      ) : (
        <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  minWidth: 120, 
                  fontWeight: 'bold',
                  backgroundColor: 'background.paper',
                  position: 'sticky',
                  left: 0,
                  zIndex: 3
                }}>
                  {t('staff_roster.employee')}
                </TableCell>
                <TableCell sx={{ 
                  minWidth: 100,
                  fontWeight: 'bold',
                  backgroundColor: 'background.paper',
                  position: 'sticky',
                  left: 120,
                  zIndex: 3
                }}>
                  {t('staff_roster.position_area')}
                </TableCell>
                {weekDays.map(day => (
                  <TableCell key={day.toString()} align="center" sx={{ 
                    minWidth: 60,
                    fontWeight: 'bold',
                    backgroundColor: day.getDay() === 0 || day.getDay() === 6 ? 'action.hover' : 'background.paper'
                  }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                        {formatDate(day, 'weekday')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(day, 'date')}
                      </Typography>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {staff.map((employee) => {
                const employeeWeekData = roster[weekKey]?.[employee.id] || {};
                return (
                  <TableRow key={employee.id} hover>
                    <TableCell component="th" scope="row" sx={{ 
                      position: 'sticky',
                      left: 0,
                      backgroundColor: 'background.paper',
                      zIndex: 2
                    }}>
                      <Typography variant="body2" fontWeight="bold">
                        {employee.name || `${employee.firstName} ${employee.lastName}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(`roles.${employee.role}`)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ 
                      position: 'sticky',
                      left: 120,
                      backgroundColor: 'background.paper',
                      zIndex: 2
                    }}>
                      <Typography variant="caption">
                        {employee.department || employee.position || '-'}
                      </Typography>
                    </TableCell>
                    {weekDays.map(day => {
                      const dateKey = formatDate(day);
                      const currentValue = employeeWeekData[dateKey] || '';
                      const shiftOption = SHIFT_OPTIONS.find(opt => opt.value === currentValue);
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      
                      return (
                        <TableCell key={dateKey} align="center" sx={{ 
                          backgroundColor: isWeekend ? 'action.hover' : 'background.paper',
                          p: 0.5
                        }}>
                          <Tooltip 
                            title={shiftOption?.label || t('staff_roster.select_shift')}
                            placement="top"
                          >
                            <Select
                              value={currentValue}
                              onChange={(e) => handleRosterChange(employee.id, dateKey, e.target.value)}
                              displayEmpty
                              size="small"
                              sx={{ 
                                minWidth: 45,
                                maxWidth: 55,
                                height: 32,
                                fontSize: '0.75rem',
                                backgroundColor: shiftOption?.color || 'transparent',
                                '& .MuiSelect-select': { 
                                  py: 0.5,
                                  textAlign: 'center',
                                  fontSize: '0.75rem'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  border: 'none'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  border: '1px solid #ccc'
                                }
                              }}
                              renderValue={(selected) => {
                                const opt = SHIFT_OPTIONS.find(o => o.value === selected);
                                return (
                                  <Box sx={{ 
                                    fontWeight: 'bold',
                                    color: selected ? 'text.primary' : 'text.disabled'
                                  }}>
                                    {opt?.short || '—'}
                                  </Box>
                                );
                              }}
                            >
                              {SHIFT_OPTIONS.map(opt => (
                                <MenuItem 
                                  key={opt.value} 
                                  value={opt.value}
                                  sx={{ 
                                    fontSize: '0.75rem',
                                    minHeight: 32
                                  }}
                                >
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    width: '100%'
                                  }}>
                                    <Box 
                                      sx={{ 
                                        width: 12, 
                                        height: 12, 
                                        borderRadius: '50%', 
                                        backgroundColor: opt.color,
                                        border: '1px solid #ccc'
                                      }} 
                                    />
                                    <Box sx={{ flex: 1 }}>
                                      {opt.label}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {opt.short}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Легенда */}
      <Box sx={{ mt: 2, p: 1, backgroundColor: 'background.default', borderRadius: 1 }}>
        <Typography variant="caption" fontWeight="bold" display="block" mb={1}>
          {t('staff_roster.legend')}:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {SHIFT_OPTIONS.filter(opt => opt.value).map(opt => (
            <Chip
              key={opt.value}
              label={`${opt.short} - ${opt.label}`}
              size="small"
              sx={{ 
                backgroundColor: opt.color,
                fontSize: '0.7rem',
                height: 24
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Діалог масових дій */}
      <Dialog open={bulkActionDialog} onClose={() => setBulkActionDialog(false)}>
        <DialogTitle>{t('staff_roster.bulk_actions')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t('staff_roster.bulk_actions_desc')}</Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('staff_roster.select_shift')}</InputLabel>
            <Select
              value={selectedShift}
              label={t('staff_roster.select_shift')}
              onChange={(e) => setSelectedShift(e.target.value)}
            >
              {SHIFT_OPTIONS.filter(shift => shift.value).map(shift => (
                <MenuItem key={shift.value} value={shift.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: shift.color }} />
                    {shift.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button 
                fullWidth 
                variant="outlined"
                onClick={() => applyBulkShift(selectedShift)}
              >
                {t('staff_roster.apply_all_week')}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button 
                fullWidth 
                variant="outlined"
                onClick={() => applyBulkShift(selectedShift, [0,1,2,3,4])}
              >
                {t('staff_roster.apply_weekdays')}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialog(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Діалог копіювання тижня */}
      <Dialog open={copyWeekDialog} onClose={() => setCopyWeekDialog(false)}>
        <DialogTitle>{t('staff_roster.copy_week')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('staff_roster.copy_week_desc')} {formatDate(addDays(startOfCurrentWeek, -7), 'full')}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyWeekDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={copyFromPreviousWeek} variant="contained">
            {t('staff_roster.copy')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог шаблонів */}
      <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)}>
        <DialogTitle>{t('staff_roster.templates')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t('staff_roster.templates_desc')}</Typography>
          
          <FormControl fullWidth>
            <InputLabel>{t('staff_roster.select_template')}</InputLabel>
            <Select
              value={selectedTemplate}
              label={t('staff_roster.select_template')}
              onChange={(e) => setSelectedTemplate(e.target.value)}
            >
              {Object.entries(SHIFT_TEMPLATES).map(([key, template]) => (
                <MenuItem key={key} value={key}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedTemplate && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('staff_roster.template_preview')}:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {SHIFT_TEMPLATES[selectedTemplate].pattern.map((shift, index) => {
                  const shiftOpt = SHIFT_OPTIONS.find(s => s.value === shift);
                  return (
                    <Chip
                      key={index}
                      label={`${formatDate(weekDays[index], 'weekday')}: ${shiftOpt?.short || '-'}`}
                      size="small"
                      sx={{ backgroundColor: shiftOpt?.color }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog(false)}>{t('common.cancel')}</Button>
          <Button 
            onClick={() => applyTemplate(selectedTemplate)} 
            variant="contained"
            disabled={!selectedTemplate}
          >
            {t('staff_roster.apply_template')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default StaffRosterPage;